import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ShieldAlert, Wind, Plus } from 'lucide-react';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Button,
  Badge,
} from '@roger/ui-components';
import { listInfectionLogs, listFallLogs } from '../../services/api';
import type {
  InfectionLogSummaryResponse,
  FallLogResponse,
  AvailableClinician,
  ClinicPhysicianResponse,
} from '../../services/api';
import type { EpisodeSummaryResponse } from '../../services/api/types.gen';
import { AddInfectionLogDialog } from './AddInfectionLogDialog';
import { AddFallLogDialog } from './AddFallLogDialog';
import {
  formatDateToMMDDYYYY,
  getBusinessTodayDate,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { getClinicianDisplayName } from '../../utils/clinicianUtils';
import { INFECTION_TYPE_LABELS } from './infectionConstants';

type EventSubTab = 'falls' | 'infections';

interface EventsTabProps {
  patientId: string;
  episodeId?: string | null;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /** All of the patient's episodes — logs from any episode appear in the lists */
  episodes: EpisodeSummaryResponse[];
  clinicians: AvailableClinician[];
  physicians: ClinicPhysicianResponse[];
}

// ── Infection display maps ────────────────────────────────────────────────────
// INFECTION_TYPE_LABELS imported from infectionConstants.ts

const INFECTION_STATUS_VARIANT: Record<
  string,
  'default' | 'secondary' | 'outline' | 'destructive'
> = {
  investigating: 'secondary',
  probable: 'outline',
  confirmed: 'default',
  resolved: 'secondary',
};

// ── Fall display maps ─────────────────────────────────────────────────────────

const INJURY_LABELS: Record<string, string> = {
  no_injury: 'No Injury',
  injury_except_major: 'Injury (Except Major)',
  major_injury: 'Major Injury',
};

const NATURE_LABELS: Record<string, string> = {
  attended: 'Attended',
  unattended: 'Unattended',
};

// ── Shared ────────────────────────────────────────────────────────────────────

const EmptyEventState: React.FC<{ icon: React.ReactNode; message: string }> = ({
  icon,
  message,
}) => (
  <div className='flex flex-col items-center justify-center py-16 text-muted-foreground'>
    <div className='mb-4 opacity-30'>{icon}</div>
    <p className='text-base font-medium'>{message}</p>
  </div>
);

export const EventsTab: React.FC<EventsTabProps> = ({
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  episodes,
  clinicians,
  physicians,
}) => {
  const [subTab, setSubTab] = useState<EventSubTab>('falls');

  // Events can only be logged once the episode has started — there would be
  // no selectable date otherwise (event dates are restricted to the episode).
  // Compared against the server's business date (Pacific) so the gate agrees
  // with backend validation.
  const episodeStart = parseISOToLocalDate(episodeStartDate);
  const episodeStarted =
    !!episodeStart && episodeStart <= getBusinessTodayDate();

  // ── Falls state ─────────────────────────────────────────────────────────────
  const [fallLogs, setFallLogs] = useState<FallLogResponse[]>([]);
  const [fallsLoading, setFallsLoading] = useState(true);
  const [addFallOpen, setAddFallOpen] = useState(false);
  const [editingFallLog, setEditingFallLog] = useState<FallLogResponse | null>(
    null
  );

  const fetchFallLogs = useCallback(async () => {
    setFallsLoading(true);
    try {
      const res = await listFallLogs({
        path: { patient_id: patientId },
      });
      setFallLogs(res.data ?? []);
    } catch (err) {
      console.error('Failed to load fall logs:', err);
    } finally {
      setFallsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchFallLogs();
  }, [fetchFallLogs]);

  // ── Infections state ─────────────────────────────────────────────────────────
  const [infectionLogs, setInfectionLogs] = useState<
    InfectionLogSummaryResponse[]
  >([]);
  const [infectionsLoading, setInfectionsLoading] = useState(true);
  const [addInfectionOpen, setAddInfectionOpen] = useState(false);
  // Infection rows open the edit dialog directly (no read-only detail step);
  // the dialog fetches the full log from this id.
  const [editingLogId, setEditingLogId] = useState<string | null>(null);

  const fetchInfectionLogs = useCallback(async () => {
    setInfectionsLoading(true);
    try {
      const res = await listInfectionLogs({
        path: { patient_id: patientId },
      });
      setInfectionLogs(res.data ?? []);
    } catch (err) {
      console.error('Failed to load infection logs:', err);
    } finally {
      setInfectionsLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchInfectionLogs();
  }, [fetchInfectionLogs]);

  // When editing, date bounds come from the log's own episode — the lists
  // show logs from every episode, not just the currently selected one. If the
  // episode can't be resolved (still loading, or archived/removed) the dialog
  // still opens, just with relaxed date bounds, so the log stays editable and
  // deletable. The backend re-validates any date change, so this is safe.
  const editingFallEpisode = useMemo(
    () => episodes.find(e => e.id === editingFallLog?.episode_id),
    [episodes, editingFallLog?.episode_id]
  );
  const editingInfectionEpisode = useMemo(() => {
    const summary = infectionLogs.find(l => l.id === editingLogId);
    return episodes.find(e => e.id === summary?.episode_id);
  }, [episodes, infectionLogs, editingLogId]);

  return (
    <div className='space-y-2'>
      <Tabs
        value={subTab}
        onValueChange={v => setSubTab(v as EventSubTab)}
        className='w-full'
      >
        <TabsList>
          <TabsTrigger value='falls'>Falls</TabsTrigger>
          <TabsTrigger value='infections'>Infections</TabsTrigger>
        </TabsList>

        {/* ── Falls Tab ─────────────────────────────────────────────────────── */}
        <TabsContent value='falls'>
          <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
            <div className='flex items-center justify-end gap-2 border-b border-border/60 bg-muted/20 px-3 py-1.5'>
              {!episodeStarted && (
                <span className='text-xs text-muted-foreground'>
                  {episodeStart
                    ? 'Fall logs can be added once the episode starts.'
                    : 'Fall logs require an episode.'}
                </span>
              )}
              <Button
                size='sm'
                variant='outline'
                onClick={() => setAddFallOpen(true)}
                disabled={!episodeStarted}
              >
                <Plus className='h-3.5 w-3.5 mr-1' />
                Add Fall Log
              </Button>
            </div>
            <div className='hidden lg:grid lg:grid-cols-[120px_1fr_1fr_1fr] items-center gap-3 border-b border-border/60 bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>
              <span>Date</span>
              <span>Nature</span>
              <span>Injury</span>
              <span>Logged By</span>
            </div>
            {fallsLoading ? (
              <div className='py-8 text-center text-sm text-muted-foreground'>
                Loading…
              </div>
            ) : fallLogs.length === 0 ? (
              <EmptyEventState
                icon={<Wind className='h-12 w-12' />}
                message='No falls recorded for this patient'
              />
            ) : (
              <div className='divide-y divide-border/60'>
                {fallLogs.map(log => (
                  <div
                    key={log.id}
                    className='grid grid-cols-1 lg:grid-cols-[120px_1fr_1fr_1fr] gap-x-3 gap-y-1 px-3 py-2.5 text-sm cursor-pointer hover:bg-muted/30 transition-colors'
                    onClick={() => setEditingFallLog(log)}
                  >
                    <span className='font-medium'>
                      {log.fall_date
                        ? formatDateToMMDDYYYY(
                            parseISOToLocalDate(log.fall_date)
                          )
                        : '—'}
                    </span>
                    <span>
                      {NATURE_LABELS[log.nature_of_fall] ?? log.nature_of_fall}
                    </span>
                    <span>
                      {INJURY_LABELS[log.injury_level] ?? log.injury_level}
                    </span>
                    <span className='text-muted-foreground'>
                      {log.created_by_name ?? '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── Infections Tab ────────────────────────────────────────────────── */}
        <TabsContent value='infections'>
          <div className='overflow-hidden rounded-md border border-border/60 bg-background/40'>
            <div className='flex items-center justify-end gap-2 border-b border-border/60 bg-muted/20 px-3 py-1.5'>
              {!episodeStarted && (
                <span className='text-xs text-muted-foreground'>
                  {episodeStart
                    ? 'Infection logs can be added once the episode starts.'
                    : 'Infection logs require an episode.'}
                </span>
              )}
              <Button
                size='sm'
                variant='outline'
                onClick={() => setAddInfectionOpen(true)}
                disabled={!episodeStarted}
              >
                <Plus className='h-3.5 w-3.5 mr-1' />
                Add Infection Log
              </Button>
            </div>
            <div className='hidden lg:grid lg:grid-cols-[100px_1fr_110px_1fr_110px_1fr] gap-x-3 items-center border-b border-border/60 bg-muted/30 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-muted-foreground'>
              <span>Date</span>
              <span>Type</span>
              <span>Status</span>
              <span>Reported By</span>
              <span>Follow-Up Due</span>
              <span>Assignee</span>
            </div>
            {infectionsLoading ? (
              <div className='py-8 text-center text-sm text-muted-foreground'>
                Loading…
              </div>
            ) : infectionLogs.length === 0 ? (
              <EmptyEventState
                icon={<ShieldAlert className='h-12 w-12' />}
                message='No infections recorded for this patient'
              />
            ) : (
              <div className='divide-y divide-border/60'>
                {infectionLogs.map(log => (
                  <div
                    key={log.id}
                    className='grid grid-cols-1 lg:grid-cols-[100px_1fr_110px_1fr_110px_1fr] gap-x-3 gap-y-1 px-3 py-2.5 text-sm cursor-pointer hover:bg-muted/30 transition-colors'
                    onClick={() => setEditingLogId(log.id)}
                  >
                    <span className='font-medium'>
                      {log.date_reported
                        ? formatDateToMMDDYYYY(
                            parseISOToLocalDate(log.date_reported)
                          )
                        : '—'}
                    </span>
                    <span>
                      {INFECTION_TYPE_LABELS[log.infection_type] ??
                        log.infection_type}
                      {log.infection_type === 'other' &&
                        log.infection_type_other && (
                          <span className='text-muted-foreground ml-1'>
                            ({log.infection_type_other})
                          </span>
                        )}
                    </span>
                    <span>
                      <Badge
                        variant={
                          INFECTION_STATUS_VARIANT[log.infection_status] ??
                          'outline'
                        }
                        className='capitalize text-[10px]'
                      >
                        {log.infection_status}
                      </Badge>
                    </span>
                    <span className='text-muted-foreground'>
                      {log.organism_reported_by_name ?? '—'}
                    </span>
                    <span className='text-muted-foreground'>
                      {log.follow_up_due_date
                        ? formatDateToMMDDYYYY(
                            parseISOToLocalDate(log.follow_up_due_date)
                          )
                        : '—'}
                    </span>
                    <span className='text-muted-foreground'>
                      {log.follow_up_assignee_id
                        ? (() => {
                            const c = clinicians.find(
                              cl => cl.id === log.follow_up_assignee_id
                            );
                            return c ? getClinicianDisplayName(c) : '—';
                          })()
                        : '—'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ── Falls dialogs ───────────────────────────────────────────────────── */}
      {/* New fall log */}
      <AddFallLogDialog
        open={addFallOpen}
        onClose={() => setAddFallOpen(false)}
        patientId={patientId}
        episodeId={episodeId}
        episodeStartDate={episodeStartDate}
        episodeEndDate={episodeEndDate}
        clinicians={clinicians}
        physicians={physicians}
        onCreated={() => {
          setAddFallOpen(false);
          fetchFallLogs();
        }}
      />
      {/* Edit existing fall log */}
      <AddFallLogDialog
        open={editingFallLog !== null}
        onClose={() => setEditingFallLog(null)}
        patientId={patientId}
        episodeId={episodeId}
        episodeStartDate={editingFallEpisode?.start_date}
        episodeEndDate={editingFallEpisode?.end_date}
        clinicians={clinicians}
        physicians={physicians}
        editingFallLog={editingFallLog}
        onCreated={() => {
          setEditingFallLog(null);
          fetchFallLogs();
        }}
      />

      {/* ── Infections dialogs ──────────────────────────────────────────────── */}
      <AddInfectionLogDialog
        open={addInfectionOpen}
        onClose={() => setAddInfectionOpen(false)}
        patientId={patientId}
        episodeId={episodeId}
        episodeStartDate={episodeStartDate}
        episodeEndDate={episodeEndDate}
        clinicians={clinicians}
        physicians={physicians}
        onCreated={() => {
          setAddInfectionOpen(false);
          fetchInfectionLogs();
        }}
      />
      <AddInfectionLogDialog
        open={editingLogId !== null}
        onClose={() => setEditingLogId(null)}
        patientId={patientId}
        episodeId={episodeId}
        episodeStartDate={editingInfectionEpisode?.start_date}
        episodeEndDate={editingInfectionEpisode?.end_date}
        clinicians={clinicians}
        physicians={physicians}
        editingLogId={editingLogId}
        onCreated={() => {
          setEditingLogId(null);
          fetchInfectionLogs();
        }}
      />
    </div>
  );
};
